import moment from "moment";
import { AppointmentTag } from "../../../types/appointmentTags";

/** Preset date ranges offered in the filter popover. Today is the default. */
export type RangeKey = "today" | "yesterday" | "last7" | "last30" | "month";

export const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "month", label: "This month" },
];

export const RANGE_LABEL: Record<RangeKey, string> = RANGE_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<RangeKey, string>,
);

/** Resolve a preset to inclusive [start, end] moments for the backend query. */
export function resolveRange(range: RangeKey): [moment.Moment, moment.Moment] {
  const end = moment().endOf("day");
  switch (range) {
    case "today":
      return [moment().startOf("day"), end];
    case "yesterday":
      return [
        moment().subtract(1, "day").startOf("day"),
        moment().subtract(1, "day").endOf("day"),
      ];
    case "last7":
      return [moment().subtract(6, "days").startOf("day"), end];
    case "last30":
      return [moment().subtract(29, "days").startOf("day"), end];
    case "month":
      return [moment().startOf("month"), end];
  }
}

export const toIso = (m: moment.Moment): string => m.format("YYYY-MM-DD");

/** Shared filter state (per screen). `status` default differs per screen. */
export interface SalesFilters {
  range: RangeKey;
  status: string;
  txn: string;
  client: string;
  /** Last 4 digits of card filter (digits only, ≤4). Empty = no filter. */
  last4: string;
  /** Appointment tag ids; a row matches when it has ANY of them. Empty = no filter. */
  tagIds: string[];
}

/** The value that means "no status filter" — differs per screen. */
export function defaultFilters(defaultStatus: string): SalesFilters {
  return {
    range: "last30",
    status: defaultStatus,
    txn: "",
    client: "",
    last4: "",
    tagIds: [],
  };
}

/**
 * Tag filter predicate (client-side; the sales endpoint has no tag param).
 * Matches when the row carries any selected tag. Rows from before the tags
 * deploy have no `tags` and only match an empty filter.
 */
export function matchesTagFilter(
  tags: AppointmentTag[] | undefined,
  tagIds: string[],
): boolean {
  if (tagIds.length === 0) return true;
  return (tags || []).some((tag) => tagIds.includes(tag.id));
}

/**
 * Drop selected tag ids that are no longer in the catalog (a deleted tag, or
 * a location switch), so the filter never silently matches nothing. Returns
 * the same object when nothing changes.
 */
export function pruneTagIds(
  filters: SalesFilters,
  catalog: AppointmentTag[],
): SalesFilters {
  const known = new Set(catalog.map((t) => t.id));
  return filters.tagIds.every((id) => known.has(id))
    ? filters
    : { ...filters, tagIds: filters.tagIds.filter((id) => known.has(id)) };
}
